import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useTheme } from '../theme/ThemeContext';
import { usePermissions } from '../context/PermissionContext';
import { ROUTES } from '../constants/routes';
import useBedInventoryStore, { selectBoardTree, selectInventoryStats } from '../stores/bedInventoryStore';
import useBedCategoryStore from '../stores/bedCategoryStore';
import AdminPageHeader from '../components/common/AdminPageHeader';
import BedBoard from '../components/bedboard/BedBoard';
import InventoryStatsStrip from '../components/bedboard/InventoryStatsStrip';
import './ProcedureCatalogPage.css';

// B3 / S7 — Static bed-board PREVIEW. Read-only render of the B3 ward→room→bed
// (+bay) inventory: every bed VACANT/uncolored, no live state, no patients.
// The SAME BedBoard component EPIC C2 later animates with live occupancy.
// Reads the same three list endpoints via the ONE shared store (single source
// of the derived tree). NON-scoped reader — shows ALL wards regardless of
// department_id (its optional ward filter is a display convenience, not a scope).

export default function BedBoardPage() {
  const { isDark } = useTheme();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const wards = useBedInventoryStore((s) => s.wards);
  const rooms = useBedInventoryStore((s) => s.rooms);
  const beds = useBedInventoryStore((s) => s.beds);
  const isLoading = useBedInventoryStore((s) => s.isLoading);
  const error = useBedInventoryStore((s) => s.error);
  const hydrate = useBedInventoryStore((s) => s.hydrate);
  const clearError = useBedInventoryStore((s) => s.clearError);

  const bedCategories = useBedCategoryStore((s) => s.bedCategories);
  const fetchBedCategories = useBedCategoryStore((s) => s.fetchBedCategories);

  const [wardFilter, setWardFilter] = useState(''); // '' = all wards (display only)

  // Single-flight store hydrates — stable deps (zustand action identities); a
  // re-render can't storm requests (bounded: 3 inventory lists + 1 category list).
  useEffect(() => { hydrate(); }, [hydrate]);
  useEffect(() => { fetchBedCategories(); }, [fetchBedCategories]);

  const tree = useMemo(
    () => selectBoardTree({ wards, rooms, beds }),
    [wards, rooms, beds],
  );
  const shownTree = useMemo(
    () => (wardFilter ? tree.filter((n) => n.ward.ward_id === wardFilter) : tree),
    [tree, wardFilter],
  );

  // B7 — ONE shared selector feeds the strip AND BedBoard's per-ward count
  // (single source of truth). Org-wide, ACTIVE-only totals regardless of the
  // display ward-filter (RC-5: the strip is never the filtered subset).
  const stats = useMemo(
    () => selectInventoryStats({ wards, rooms, beds }),
    [wards, rooms, beds],
  );
  const categoryById = useMemo(() => {
    const m = {};
    for (const c of bedCategories || []) m[c.bed_category_id] = c;
    return m;
  }, [bedCategories]);
  const filteredWardName = wardFilter
    ? (tree.find((n) => n.ward.ward_id === wardFilter)?.ward.name || '')
    : '';

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  // Gate — Admin-only in B3 (S7 audience = Admin; the MANAGE_BED_CONFIG list
  // endpoints enforce it server-side too). C2 broadens the read audience with a
  // dedicated VIEW permission WHEN its live-occupancy board ships — not now.
  if (!hasPermission('MANAGE_BED_CONFIG')) {
    return <Navigate to={ROUTES.ORG} replace />;
  }

  return (
    <div className="pcp-page" style={{ background: bg }}>
      <AdminPageHeader />
      <main className="pcp-main">
        <div className="pcp-title-row">
          <div>
            <h1 className="pcp-page-title" style={{ color: isDark ? '#f5f5f7' : '#111827' }}>
              🛏 Bed Board (preview)
            </h1>
            <p className="pcp-page-subtitle" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
              A read-only preview of your bed inventory. Every bed shows as vacant — live occupancy
              arrives with admissions.
            </p>
          </div>
          <button className="pcp-btn pcp-btn-secondary" onClick={() => navigate(ROUTES.BED_SETUP)}>
            Edit inventory
          </button>
        </div>

        {isLoading ? (
          <div className="pcp-state">Loading…</div>
        ) : error ? (
          <div className="pcp-state pcp-state-err">
            {error}
            <button
              className="pcp-btn pcp-btn-secondary"
              onClick={() => { clearError(); hydrate({ force: true }); }}
            >
              Retry
            </button>
          </div>
        ) : tree.length === 0 ? (
          <div className="pcp-state">
            🛏 No wards or beds yet — build them in Bed &amp; Tariff Setup.
            <button className="pcp-btn pcp-btn-primary" onClick={() => navigate(ROUTES.BED_SETUP)}>
              Go to Bed &amp; Tariff Setup
            </button>
          </div>
        ) : (
          <>
            <InventoryStatsStrip
              stats={stats}
              categoryById={categoryById}
              note={wardFilter
                ? `Organisation-wide totals — board showing ${filteredWardName} only.`
                : null}
            />
            <div className="pcp-filter-bar">
              <label className="pcp-checkbox">
                Ward:&nbsp;
                <select
                  aria-label="Ward filter"
                  value={wardFilter}
                  onChange={(e) => setWardFilter(e.target.value)}
                >
                  <option value="">All wards</option>
                  {tree.map((n) => (
                    <option key={n.ward.ward_id} value={n.ward.ward_id}>{n.ward.name}</option>
                  ))}
                </select>
              </label>
            </div>
            <BedBoard tree={shownTree} perWard={stats.perWard} />
          </>
        )}
      </main>
    </div>
  );
}
